import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Drawer from '../../../../components/drawer/drawer';
import Form, { SubmitType } from '../../../../components/form';
import useCreateFooter from './use-create-footer';
import { dealSchema, useDealForm } from './use-deal-form';
import { movedSchema, useMovedForm } from './moved/use-moved-drawer-form';
import { useWarehouseContext } from './warehouse-context';
import { UseFormReturn } from 'react-hook-form';
import { of } from 'rxjs';
import DealProduct from '../../../../interfaces/deal-product';
import ClassifierValue from '../../../../interfaces/classifierValue';
import { Product } from '../../../../interfaces/product';
import { ProductObtainType } from '../../../../interfaces/product-obtain-type';
import DealType from '../../../../enums/deal-type';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useService } from '../../../../services/services';
import CreateWarehouseModalForm from '../components/warehouse/create-warehouse-modal-form';
import css from './drawer-form.module.css';
import ObtainTypes from '../../../../enums/obtain-types';

const DEFAULT_OBTAIN_TYPE_BY_DEAL_TYPE = {
  [DealType.purchased]: ObtainTypes.purchased,
  [DealType.produced]: ObtainTypes.produced,
  [DealType.recycled]: ObtainTypes.recycle_gained,
}

const DrawerForm = ({
  formMethods,
  drawerModalFormMethods,
  type,
  keyValue = 'products',
}: {
  formMethods: UseFormReturn;
  drawerModalFormMethods?: UseFormReturn;
  type?: string;
  keyValue?: string;
}) => {
  const {
    isOpen,
    setIsOpen,
    openModal,
    formValues,
    setFormValues,
    setExceptionProducts,
    PRODUCT_OBTAIN_REQUIRED,
  } = useWarehouseContext();
  const { t } = useTranslation();
  const [warehouseId, setWarehouseId] = useState('');
  const { dealType, farmId } = useParams<{
    dealType: DealType;
    farmId: string;
  }>();
  const { dealService } = useService();

  const PRODUCT_OBTAIN_CLASSIFIERS = [DealType.recycled];

  const isProductObtainClassifiersRequired =
    PRODUCT_OBTAIN_CLASSIFIERS.includes(dealType);

  const dealStateTemp: DealProduct = useMemo(
    () => ({
      packaging_size: '',
      packaging_type_id: { value: '', label: '' },
      packaging_unit: { label: '', value: '' },
      packaging_amount: '',
      packaging_price: '',
      partner_id: { value: '', label: '' },
      is_partner_required: false,
      purchase_document_number: '',
      type: { label: '', value: '' },
      product_category_id: { label: '', value: '', type: 'any' },
      vat_sum: '',
      product_obtain_type_id: DEFAULT_OBTAIN_TYPE_BY_DEAL_TYPE[dealType] ?? '',
      product_type: {
        id: '',
        classifer_id: '',
        parent_id: '',
        parent: '',
        value: '',
      } as unknown as ClassifierValue,
      product_type_id: { label: '', value: '', veterinary: false },
      product_id: { label: '', value: '', obtain_type_id: '' },
      amount: null,
      unit: { label: '', value: '' },
      to_warehouse_id: { label: '', value: '' },
      product_warehouses: [],
      price: '',
      sum: '',
      vat_rate: {
        label: '',
        value: '',
        rate: '',
      } as unknown as ClassifierValue,
      vat: '',
      sum_with_vat: '',
      id: '',
      product_obtain_type: { name: '' } as unknown as ProductObtainType,
      'product_obtain_type.name': '',
      'product_category.value': '',
      product_category: {
        value: '',
        name: '',
      } as unknown as ClassifierValue,
      'to_warehouse_id.name': '',
      'to_warehouse_id.label': '',
      'to_warehouse_id.value': '',
      'product_type.value': '',
      'product_type_id.label': '',
      'product.name': '',
      'product_id.label': '',
      product: { name: '' } as unknown as Product,
      packaging_type: {
        id: '',
        classifer_id: '',
        parent_id: '',
        parent: '',
        value: '',
      } as unknown as ClassifierValue,
      price_amount: '',
      vat_rate_id: '',
      vat_amount: '',
      'vat_rate.label': '',
    }),
    [dealType]
  );

  const [dealState, setDealState] = useState<DealProduct>(dealStateTemp);

  const handleCloseDrawer = useCallback(() => {
    setDealState(dealStateTemp);
    setFormValues(dealStateTemp);
    setIsOpen(false);
    setExceptionProducts('');
    drawerModalFormMethods.reset();
    drawerModalFormMethods.setValue('product_id', '');
    drawerModalFormMethods.setValue('product_type_id', '');
    drawerModalFormMethods.setValue('product_category_id', '');
  }, [
    setDealState,
    setFormValues,
    setExceptionProducts,
    setIsOpen,
    dealStateTemp,
    drawerModalFormMethods,
  ]);

  const submit = useCallback<SubmitType<DealProduct>>(
    (form) => {
      let currentProducts = formMethods.getValues(keyValue);
      if (!form?.id || currentProducts.length === 0) {
        form.id = form.id
          ? form.id
          : `${form?.product_warehouses?.[0]?.warehouse_id?.value}_${
              form?.product_id?.value
            }_${Date.now()}`;
        currentProducts.push(form);
      } else {
        currentProducts = currentProducts.map((product) =>
          product.id === form.id ? form : product
        );
      }

      formMethods.setValue(keyValue, currentProducts);

      if (currentProducts[0]?.id) {
        formMethods.setValue('products_exists', true);
        formMethods.trigger();
      }

      handleCloseDrawer();

      return [of(null)];
    },
    [
      formMethods,
      handleCloseDrawer,
      keyValue,
    ]
  );

  //functionality for party_number
  useEffect(() => {
    if (
      (!formValues ||
        !formValues?.product_warehouses?.[0]?.party_number?.value) &&
      [
        DealType.purchased,
        DealType.produced,
        DealType.recycled,
        DealType.lost,
      ].includes(dealType)
    ) {
      const subscription = dealService
        .generatePartyNumber(farmId, dealType)
        .subscribe((partyNumber) => {
          drawerModalFormMethods.setValue(
            'product_warehouses.0.party_number',
            {
              value: partyNumber.party_number,
              label: partyNumber.party_number,
            },
            {
              shouldValidate: true,
            }
          );
        });
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [dealService, farmId, dealType, formValues, t, drawerModalFormMethods]);

  useEffect(() => {
    if (isOpen) {
      setDealState(!formValues?.id ? { ...dealStateTemp } : { ...formValues });
    }
  }, [isOpen, formValues, dealStateTemp, setDealState]);

  let productsCreatable =
    [DealType.purchased, DealType.produced, DealType.recycled].includes(
      dealType
    ) || formMethods.getValues('subtype') === DealType.leftover;

  const body = useDealForm(
    PRODUCT_OBTAIN_REQUIRED.includes(dealType),
    true,
    isProductObtainClassifiersRequired,
    formMethods,
    warehouseId,
    formValues,
    productsCreatable,
    keyValue
  );

  const movedBody = useMovedForm(formValues, warehouseId);

  const createFooter = useCreateFooter(handleCloseDrawer);

  return (
    <Drawer isOpen={isOpen}>
      <Form
        title={'pages.warehouse.deals.add_product'}
        mode="step"
        body={type === 'moved' ? movedBody : body}
        footer={createFooter}
        values={dealState}
        schema={
          type === 'moved'
            ? movedSchema(PRODUCT_OBTAIN_REQUIRED.includes(dealType))
            : dealSchema(PRODUCT_OBTAIN_REQUIRED.includes(dealType))
        }
        submit={submit}
        formMethods={drawerModalFormMethods}
        className={css['drawer-form']}
      />
      {openModal && (
        <CreateWarehouseModalForm
          formMethods={drawerModalFormMethods}
          setWarehouseId={setWarehouseId}
        />
      )}
    </Drawer>
  );
};

export default DrawerForm;
